import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
import { triggerRefresh } from "./refreshSlice";
const MODULE_KEY = "publisher_tagging";
// ==========================================================================
// HELPERS
// ==========================================================================
const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};
const getInitialDates = () => {
    return {
        startDate: "",
        endDate: "",
    };
};
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const { startDate: defaultStart, endDate: defaultEnd } = getInitialDates();
const initialState = {
    rows: [],
    pagination: null,
    platform: "All",
    clientIds: [],
    tag: "",
    groupPublisherName: "",
    publisherLink: "",
    startDate: defaultStart,
    endDate: defaultEnd,
    page: 1,
    limit: 100,
    sortBy: "revenue",
    sortOrder: "desc",
    filterOptions: null,
    filterOptionsLoading: false,
    groupPublisherSearch: [],
    groupPublisherSearchLoading: false,
    loading: false,
    error: null,
    updating: false,
    updateError: null,
    lastSyncedAt: null,
    refreshLoading: false,
    q: "",
};
export const fetchPublisherTagging = createAsyncThunk("publisherTagging/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/publisher-tagging/list", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to fetch publisher tagging data");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
// ── Fetch filter options ──
export const fetchPublisherTaggingFilterOptions = createAsyncThunk("publisherTagging/fetchFilterOptions", async ({ platform }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const qs = platform && platform !== "All" ? `?platform=${platform}` : "";
        const response = await ApiCall("GET", `/api/publisher-tagging/filter-options${qs}`, undefined, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to fetch filter options");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
// ── Search group publishers ──
export const searchGroupPublishers = createAsyncThunk("publisherTagging/searchGroupPublishers", async ({ q }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const qs = q ? `?q=${encodeURIComponent(q)}` : "";
        const response = await ApiCall("GET", `/api/publisher-tagging/group-publisher-search${qs}`, undefined, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success)
            return data.data;
        return rejectWithValue(data?.message || "Failed to search group publishers");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const updatePublisherTag = createAsyncThunk("publisherTagging/updateTag", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/publisher-tagging/tag", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success) {
            toast.success(data.message || "Tag updated successfully");
            return data.data;
        }
        return rejectWithValue(data?.message || "Failed to update tag");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const updateGroupPublisher = createAsyncThunk("publisherTagging/updateGroupPublisher", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/publisher-tagging/group-publisher", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success) {
            toast.success(data.message || "Group publisher updated successfully");
            return data.data;
        }
        return rejectWithValue(data?.message || "Failed to update group publisher");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
export const updatePublisherUrl = createAsyncThunk("publisherTagging/updateUrl", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/publisher-tagging/publisher-url", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success) {
            toast.success(data.message || "URL updated successfully");
            return data.data;
        }
        return rejectWithValue(data?.message || "Failed to update URL");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
// ── Bulk update ──
export const bulkUpdatePublisherTagging = createAsyncThunk("publisherTagging/bulkUpdate", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/publisher-tagging/bulk-update", payload, { Authorization: token, "x-module-key": MODULE_KEY });
        const data = response?.data;
        if (data?.success) {
            toast.success(data.message || "Changes saved successfully");
            return data.data;
        }
        return rejectWithValue(data?.message || "Failed to save changes");
    }
    catch (err) {
        return rejectWithValue(err?.response?.data?.message || err.message || "An error occurred");
    }
});
// ── Refresh ──
export const refreshPublisherTagging = createAsyncThunk("publisherTagging/refresh", async (payload, { dispatch, rejectWithValue }) => {
    const result = await dispatch(triggerRefresh({
        platform: payload.platform,
        clientId: payload.clientId,
        dateRange: [{ startDate: payload.startDate, endDate: payload.endDate }],
        moduleKey: MODULE_KEY,
    }));
    if (triggerRefresh.fulfilled.match(result)) {
        const failedNetworks = result.payload?.data?.failed_networks || [];
        if (failedNetworks.length > 0) {
            const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                .map(([name, err]) => {
                let eStr = typeof err === 'string' ? err : err?.message;
                if (eStr && eStr.toLowerCase().includes('time'))
                    eStr = 'Timeout';
                return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
            })
                .join(', ');
            toast.warning(`Publisher Tagging refresh completed.\nFailed to refresh: ${failedNames}.`);
        }
        else {
            toast.success(result.payload?.message || "Refresh triggered successfully");
        }
        return result.payload?.data ?? result.payload;
    }
    return rejectWithValue(result.payload || "Failed to trigger refresh");
});
// ==========================================================================
// SLICE
// ==========================================================================
const publisherTaggingSlice = createSlice({
    name: "publisherTagging",
    initialState,
    reducers: {
        setPlatform: (state, action) => {
            state.platform = action.payload;
            state.clientIds = []; // reset client filter when platform changes
            state.page = 1;
        },
        setClientIds: (state, action) => {
            state.clientIds = action.payload;
            state.page = 1;
        },
        setTag: (state, action) => {
            state.tag = action.payload;
            state.page = 1;
        },
        setGroupPublisherName: (state, action) => {
            state.groupPublisherName = action.payload;
            state.page = 1;
        },
        setPublisherLink: (state, action) => {
            state.publisherLink = action.payload;
            state.page = 1;
        },
        setDateRange: (state, action) => {
            state.startDate = action.payload.startDate;
            state.endDate = action.payload.endDate;
            state.page = 1;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setSortConfig: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
            state.page = 1;
        },
        resetFilters: (state) => {
            const { startDate, endDate } = getInitialDates();
            state.platform = "All";
            state.clientIds = [];
            state.tag = "";
            state.groupPublisherName = "";
            state.publisherLink = "";
            state.startDate = startDate;
            state.endDate = endDate;
            state.page = 1;
            state.q = "";
        },
        setSearchQuery: (state, action) => {
            state.q = action.payload;
            state.page = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch list
            .addCase(fetchPublisherTagging.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchPublisherTagging.fulfilled, (state, action) => {
            state.loading = false;
            state.rows = action.payload.rows || [];
            state.pagination = action.payload.pagination || null;
            state.lastSyncedAt = action.payload.lastSyncedAt || null;
            if (action.payload.startDate)
                state.startDate = action.payload.startDate;
            if (action.payload.endDate)
                state.endDate = action.payload.endDate;
        })
            .addCase(fetchPublisherTagging.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch";
            toast.error(state.error || "Failed to fetch publisher tagging data");
        })
            // Filter options
            .addCase(fetchPublisherTaggingFilterOptions.pending, (state) => {
            state.filterOptionsLoading = true;
        })
            .addCase(fetchPublisherTaggingFilterOptions.fulfilled, (state, action) => {
            state.filterOptionsLoading = false;
            state.filterOptions = action.payload;
        })
            .addCase(fetchPublisherTaggingFilterOptions.rejected, (state) => {
            state.filterOptionsLoading = false;
        })
            // Group publisher search
            .addCase(searchGroupPublishers.pending, (state) => {
            state.groupPublisherSearchLoading = true;
        })
            .addCase(searchGroupPublishers.fulfilled, (state, action) => {
            state.groupPublisherSearchLoading = false;
            state.groupPublisherSearch = action.payload;
        })
            .addCase(searchGroupPublishers.rejected, (state) => {
            state.groupPublisherSearchLoading = false;
        })
            // Update tag
            .addCase(updatePublisherTag.pending, (state) => {
            state.updating = true;
            state.updateError = null;
        })
            .addCase(updatePublisherTag.fulfilled, (state) => {
            state.updating = false;
        })
            .addCase(updatePublisherTag.rejected, (state, action) => {
            state.updating = false;
            state.updateError = action.payload || "Failed to update tag";
            toast.error(state.updateError || "Failed to update tag");
        })
            // Update group publisher
            .addCase(updateGroupPublisher.pending, (state) => {
            state.updating = true;
            state.updateError = null;
        })
            .addCase(updateGroupPublisher.fulfilled, (state) => {
            state.updating = false;
        })
            .addCase(updateGroupPublisher.rejected, (state, action) => {
            state.updating = false;
            state.updateError = action.payload || "Failed to update group publisher";
            toast.error(state.updateError || "Failed to update group publisher");
        })
            // Bulk update
            .addCase(bulkUpdatePublisherTagging.pending, (state) => {
            state.updating = true;
            state.updateError = null;
        })
            .addCase(bulkUpdatePublisherTagging.fulfilled, (state) => {
            state.updating = false;
        })
            .addCase(bulkUpdatePublisherTagging.rejected, (state, action) => {
            state.updating = false;
            state.updateError = action.payload || "Failed to save changes";
            toast.error(state.updateError || "Failed to save changes");
        })
            // Refresh
            .addCase(refreshPublisherTagging.pending, (state) => {
            state.refreshLoading = true;
        })
            .addCase(refreshPublisherTagging.fulfilled, (state) => {
            state.refreshLoading = false;
        })
            .addCase(refreshPublisherTagging.rejected, (state, action) => {
            state.refreshLoading = false;
            toast.error(action.payload || "Failed to trigger refresh");
        });
    },
});
// ==========================================================================
// EXPORTS
// ==========================================================================
export const { setPlatform, setClientIds, setTag, setGroupPublisherName, setPublisherLink, setDateRange, setPage, setSortConfig, resetFilters, setSearchQuery, } = publisherTaggingSlice.actions;
// Selectors
export const selectPublisherTaggingRows = (state) => state.publisherTagging.rows;
export const selectPublisherTaggingPagination = (state) => state.publisherTagging.pagination;
export const selectPublisherTaggingLoading = (state) => state.publisherTagging.loading;
export const selectPublisherTaggingError = (state) => state.publisherTagging.error;
export const selectPublisherTaggingFilters = (state) => ({
    platform: state.publisherTagging.platform,
    clientIds: state.publisherTagging.clientIds,
    tag: state.publisherTagging.tag,
    groupPublisherName: state.publisherTagging.groupPublisherName,
    publisherLink: state.publisherTagging.publisherLink,
    startDate: state.publisherTagging.startDate,
    endDate: state.publisherTagging.endDate,
    page: state.publisherTagging.page,
    limit: state.publisherTagging.limit,
    sortBy: state.publisherTagging.sortBy,
    sortOrder: state.publisherTagging.sortOrder,
    q: state.publisherTagging.q,
});
export const selectFilterOptions = (state) => state.publisherTagging.filterOptions;
export const selectFilterOptionsLoading = (state) => state.publisherTagging.filterOptionsLoading;
export const selectGroupPublisherSearch = (state) => state.publisherTagging.groupPublisherSearch;
export const selectGroupPublisherSearchLoading = (state) => state.publisherTagging.groupPublisherSearchLoading;
export const selectPublisherTaggingUpdating = (state) => state.publisherTagging.updating;
export const selectPublisherTaggingLastSyncedAt = (state) => state.publisherTagging.lastSyncedAt;
export const selectPublisherTaggingRefreshLoading = (state) => state.publisherTagging.refreshLoading;
export default publisherTaggingSlice.reducer;
